import type { TelemetryEvent } from "@nova/schemas"
import { astro } from "./formats/astro"
import { autel } from "./formats/autel"
import { dji } from "./formats/dji"
import { djiMini } from "./formats/dji-mini"
import { geodvr } from "./formats/geodvr"
import { h30t } from "./formats/h30t"
import { smooth } from "./smoothing"

export type { TelemetryEvent } from "@nova/schemas"

const formats = [autel, djiMini, dji, h30t, astro, geodvr] as const

export type TelemetryType = (typeof formats)[number]["name"]

const sniffLength = 256

const decode = (data: string | Uint8Array) =>
    typeof data === "string" ? data : new TextDecoder().decode(data)

const prefixOf = (data: string | Uint8Array) => {
    const prefix =
        typeof data === "string"
            ? data.slice(0, sniffLength)
            : new TextDecoder().decode(data.subarray(0, sniffLength))
    return prefix.replace(/^[\0\s]+/, "")
}

const matchFormat = (prefix: string) => formats.find((format) => format.matches(prefix))

export const getTelemetryType = (data: string | Uint8Array): TelemetryType | undefined =>
    matchFormat(prefixOf(data))?.name

export const parseTelemetry = (
    data: string | Uint8Array,
    options?: { minSamplesPerSecond?: number }
): TelemetryEvent[] => {
    const text = decode(data)
    const format = matchFormat(prefixOf(text))
    if (format === undefined) throw new Error("unrecognized telemetry format")
    const { events, samplesPerSecond } = format.parse(text)
    return options?.minSamplesPerSecond === undefined
        ? events
        : smooth(events, samplesPerSecond, options.minSamplesPerSecond)
}
